import { Alert, Box, Button, Checkbox, Group, Paper, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconTrash } from '@tabler/icons-react';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useContentManagerId, useUploadContentMaterials } from '../../api';
import { BulkUploadType, getNameFromFilename } from '../../constants/mediaConfig';
import { MAX_UPLOAD_FILES_PER_REQUEST } from '../../constants/upload';
import { createSessionPreviewUrl } from '../../utils/preview';
import { useContentExit } from '../ContentBackLink';
import { useContentExitQueue } from '../ContentExitContext';

import { DropZone } from './DropZone';
import { FileRow } from './FileRow';
import { FileDraft } from './types';
import { validateMediaFile } from './validateFile';

import { Platform } from '@/features/pages';
import { queryClient } from '@/libraries/react-query';
import { Departments } from '@/types';
import { COLORS } from '@/utils/colors';

type Props = {
  department: Departments;
  pageId: string;
  platform: Platform;
  mediaType: BulkUploadType;
};

const genId = () => `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

const createDraft = (file: File, mediaType: BulkUploadType, platform: Platform): FileDraft => {
  const previewUrl = createSessionPreviewUrl(file, mediaType);

  return {
    id: genId(),
    file,
    name: getNameFromFilename(file.name),
    category: '',
    status: 'active',
    selected: false,
    previewUrl,
    validation: validateMediaFile(file, mediaType, platform),
    uploadState: 'idle',
  };
};

const revokePreviewUrls = (rows: FileDraft[]) => {
  rows.forEach((row) => {
    if (row.previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(row.previewUrl);
    }
  });
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    return error.response?.data?.error || error.message || 'Ошибка загрузки';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Ошибка загрузки';
};

export const BulkUploadForm = ({ department, pageId, platform, mediaType }: Props) => {
  const navigate = useNavigate();
  const {
    managerId,
    isReady,
    needsManager,
    isLoading: isAuthLoading,
  } = useContentManagerId(department);
  const { mutateAsync: uploadMaterials } = useUploadContentMaterials({ silent: true });
  const [rows, setRows] = useState<FileDraft[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const failedRows = useMemo(() => rows.filter((row) => row.uploadState === 'error'), [rows]);
  const selectedCount = useMemo(() => rows.filter((row) => row.selected).length, [rows]);
  const hasQueue = rows.length > 0;

  useContentExitQueue(hasQueue);
  const tryExit = useContentExit(hasQueue);

  useEffect(() => {
    return () => {
      revokePreviewUrls(rowsRef.current);
    };
  }, []);

  const addFiles = (files: File[]) => {
    if (isUploading) return;

    setRows((prev) => {
      const remaining = MAX_UPLOAD_FILES_PER_REQUEST - prev.length;
      if (remaining <= 0) {
        notifications.show({
          color: 'orange',
          title: 'Лимит файлов',
          message: `Максимум ${MAX_UPLOAD_FILES_PER_REQUEST} файлов за одну загрузку`,
        });
        return prev;
      }

      const accepted = files.slice(0, remaining);
      if (files.length > remaining) {
        notifications.show({
          color: 'orange',
          title: 'Лимит файлов',
          message: `Добавлено ${remaining} из ${files.length}. Максимум ${MAX_UPLOAD_FILES_PER_REQUEST} файлов за загрузку`,
        });
      }

      return [...prev, ...accepted.map((file) => createDraft(file, mediaType, platform))];
    });
  };

  const updateRow = (id: string, patch: Partial<FileDraft>) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeRow = (id: string) => {
    const row = rows.find((item) => item.id === id);
    if (row?.previewUrl) URL.revokeObjectURL(row.previewUrl);
    setRows((prev) => prev.filter((item) => item.id !== id));
  };

  const removeSelected = () => {
    const toRevoke = rows
      .filter((row) => row.selected && row.previewUrl)
      .map((row) => row.previewUrl!);
    toRevoke.forEach((url) => URL.revokeObjectURL(url));
    setRows((prev) => prev.filter((row) => !row.selected));
  };

  const setSelectedStatus = (status: FileDraft['status']) => {
    setRows((prev) =>
      prev.map((row) => (row.selected && row.validation.ok ? { ...row, status } : row))
    );
  };

  const toggleSelectAll = (checked: boolean) => {
    setRows((prev) => prev.map((row) => ({ ...row, selected: checked })));
  };

  const uploadOne = async (id: string): Promise<boolean> => {
    if (!rowsRef.current.some((item) => item.id === id)) return true;

    updateRow(id, { uploadState: 'uploading', uploadError: undefined });
    try {
      const row = rowsRef.current.find((item) => item.id === id);
      if (!row) return true;

      await uploadMaterials({
        pageId,
        type: mediaType,
        status: 'active',
        files: [row.file],
        items: [
          {
            name: row.name.trim() || getNameFromFilename(row.file.name),
            category: row.category.trim(),
            status: row.status,
          },
        ],
        managerId,
      });
      if (row.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(row.previewUrl);
      }
      setRows((prev) => prev.filter((item) => item.id !== id));
      return true;
    } catch (error) {
      updateRow(id, {
        uploadState: 'error',
        uploadError: getErrorMessage(error),
        selected: false,
      });
      return false;
    }
  };

  const runUpload = async (targetIds: string[]) => {
    if (!targetIds.length || isUploading || !isReady) return;

    setIsUploading(true);
    let uploaded = 0;
    let errors = 0;
    const uploadedIds = new Set<string>();

    try {
      for (const id of targetIds) {
        const ok = await uploadOne(id);
        if (ok) {
          uploaded += 1;
          uploadedIds.add(id);
        } else {
          errors += 1;
        }
      }

      await queryClient.invalidateQueries(['content-materials', pageId]);

      const total = uploaded + errors;
      notifications.show({
        color: errors > 0 ? (uploaded > 0 ? 'orange' : 'red') : 'teal',
        title: 'Загрузка завершена',
        message: `Загружено ${uploaded} из ${total}. Ошибок: ${errors}`,
      });

      // rowsRef is only synced on render, so the setRows from the last
      // uploadOne may not be reflected yet. Exclude uploaded rows explicitly
      // instead of relying on render timing.
      const remainingRows = rowsRef.current.filter((row) => !uploadedIds.has(row.id));
      if (remainingRows.length === 0) {
        navigate('/content');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpload = () => {
    const targetIds = rows
      .filter((row) => row.validation.ok && row.uploadState === 'idle')
      .map((row) => row.id);
    void runUpload(targetIds);
  };

  const handleRetryRow = (id: string) => {
    const row = rowsRef.current.find((item) => item.id === id);
    if (!row || row.uploadState !== 'error') return;
    void runUpload([id]);
  };

  const handleRetryFailed = () => {
    void runUpload(failedRows.map((row) => row.id));
  };

  const pendingCount = rows.filter((row) => row.validation.ok && row.uploadState === 'idle').length;
  const uploadButtonLabel =
    failedRows.length > 0 && pendingCount === 0
      ? `Повторить ошибки (${failedRows.length})`
      : `Загрузить все (${pendingCount})`;

  return (
    <Stack gap="md">
      {needsManager && (
        <Alert color="yellow" title="Нужен менеджер">
          Создайте менеджера в разделе Managers для региона {department.toUpperCase()}, чтобы
          загружать контент.
        </Alert>
      )}

      <Paper
        p="md"
        radius="md"
        style={{
          border: `1px solid ${COLORS.primary}`,
          backgroundColor: 'var(--mantine-color-dark-7)',
        }}
      >
        <DropZone
          mediaType={mediaType}
          platform={platform}
          compact={rows.length > 0}
          isDisabled={isUploading}
          onFiles={addFiles}
        />
      </Paper>

      {rows.length > 0 && (
        <Paper
          p="md"
          radius="md"
          style={{
            border: `1px solid ${COLORS.primary}`,
            backgroundColor: 'var(--mantine-color-dark-7)',
          }}
        >
          <Group justify="space-between" mb="md">
            <Group gap="lg">
              <Text size="sm" c="dimmed">
                В очереди: {rows.length} / {MAX_UPLOAD_FILES_PER_REQUEST}
              </Text>
              <Text size="sm" c="dimmed">
                Выделено: {selectedCount}
              </Text>
              {failedRows.length > 0 && (
                <Text size="sm" c="red">
                  Ошибок: {failedRows.length}
                </Text>
              )}
            </Group>

            <Group gap="xs">
              <Button
                variant="default"
                size="xs"
                disabled={selectedCount === 0 || isUploading}
                onClick={() => setSelectedStatus('archived')}
              >
                Архивировать выделенные
              </Button>
              <Button
                variant="default"
                size="xs"
                color="red"
                leftSection={<IconTrash size={14} />}
                disabled={selectedCount === 0 || isUploading}
                onClick={removeSelected}
              >
                Удалить выделенные
              </Button>
            </Group>
          </Group>

          <Group gap="sm" mb="xs" wrap="nowrap" style={{ opacity: 0.7 }}>
            <Checkbox
              checked={rows.length > 0 && rows.every((row) => row.selected)}
              indeterminate={selectedCount > 0 && selectedCount < rows.length}
              disabled={isUploading}
              onChange={(event) => toggleSelectAll(event.currentTarget.checked)}
            />
            <Text size="xs" tt="uppercase" style={{ flex: '0 0 48px' }} />
            <Text size="xs" tt="uppercase" style={{ flex: '0 0 160px' }}>
              Файл
            </Text>
            <Text size="xs" tt="uppercase" style={{ flex: '1 1 140px' }}>
              Название
            </Text>
            <Text size="xs" tt="uppercase" style={{ flex: '0 0 160px' }}>
              Категория
            </Text>
            <Text size="xs" tt="uppercase" style={{ flex: '0 0 120px' }}>
              Статус
            </Text>
            <Text size="xs" tt="uppercase" style={{ flex: '0 0 72px' }}>
              Размер
            </Text>
            <Text size="xs" tt="uppercase" style={{ flex: '0 0 72px' }}>
              Валидация
            </Text>
            <Box w={28} />
          </Group>

          <Box>
            {rows.map((row) => (
              <FileRow
                key={row.id}
                pageId={pageId}
                mediaType={mediaType}
                row={row}
                isBatchUploading={isUploading}
                onChange={(patch) => updateRow(row.id, patch)}
                onRemove={() => removeRow(row.id)}
                onRetry={row.uploadState === 'error' ? () => handleRetryRow(row.id) : undefined}
              />
            ))}
          </Box>
        </Paper>
      )}

      <Group justify="flex-end">
        <Button variant="default" onClick={tryExit} disabled={isUploading}>
          Отмена
        </Button>
        {failedRows.length > 0 && pendingCount > 0 && (
          <Button variant="light" color="orange" onClick={handleRetryFailed} loading={isUploading}>
            Повторить ошибки ({failedRows.length})
          </Button>
        )}
        <Button
          onClick={failedRows.length > 0 && pendingCount === 0 ? handleRetryFailed : handleUpload}
          loading={isUploading}
          disabled={(pendingCount === 0 && failedRows.length === 0) || !isReady || isAuthLoading}
        >
          {uploadButtonLabel}
        </Button>
      </Group>
    </Stack>
  );
};
